import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminRolesPageStyles.scss";
import CPButton from "shared/components/shared/CPButton/CPButton";
import RoleDetailsModal, { RoleRecord } from "./RoleDetailsModal";
import { Box, CPText } from "shared/components/base";
import { Chip } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import {
  useResourceCategoriesLookupQuery,
  useResourcesLookupQuery,
} from "data/lookup/lookupQueries";
import { useQuery, useQueryClient } from "react-query";
import { GridFetchResponse } from "shared/types";
import http from "shared/http";
import { useGetUsersForRoleQuery } from "data/role/role";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";

export interface UseGetRolesForGridQueryOptions {
  name?: string;
  externalSsoGroupId?: string;
  category?: string;
  resource?: string;
  firstname?: string;
  lastname?: string;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

export const useGetRolesForGridQuery = (
  params: UseGetRolesForGridQueryOptions,
) => {
  return useQuery(
    ["roles-grid", JSON.stringify(params)],
    async (): Promise<GridFetchResponse> => {
      return await http.fetchAsync("admin/roles/search", params);
    },
    {
      staleTime: 30 * 1000,
    },
  );
};

//Columns
interface ColumnHandlers {
  onEdit: (role: RoleRecord) => void;
  onToggleExpand: (id: number) => void;
}

// Inline detail panel rendered beneath an expanded row
const RoleDetailRow: React.FC<{ roleId: number; colSpan: number }> = ({
  roleId,
  colSpan,
}) => {
  const { data: usersData, isLoading } = useGetUsersForRoleQuery(roleId);

  const [userFilter, setUserFilter] = React.useState("");
  const PAGE_SIZE = 50;

  const filteredUsers = React.useMemo(() => {
    if (!usersData?.assigned) return [];
    const q = userFilter.trim().toLowerCase();
    if (!q) return usersData.assigned;
    return usersData.assigned.filter((u) =>
      `${u.firstname} ${u.lastname}`.toLowerCase().includes(q),
    );
  }, [usersData, userFilter]);

  return (
    <tr className="role-detail-row">
      <td colSpan={colSpan} className="role-detail-cell">
        {isLoading ? (
          <Box
            className="role-detail-loading"
            flexDirection="row"
            alignItems="center"
            spacing={8}
          >
            <Loader size="small" type="converging-spinner" />
            <span>Loading…</span>
          </Box>
        ) : (
          <Box className="role-detail-content">
            <Box className="role-detail-section" flexDirection="column">
              <span className="role-detail-label">
                <i className="fa-solid fa-user-group" /> Users assigned
              </span>
              <Box style={{ width: 250, marginBottom: 8 }}>
                <CPTextBox
                  name="roleUserFilter"
                  placeholder="Filter users…"
                  value={userFilter}
                  onChange={(e) => setUserFilter((e.value as string) ?? "")}
                />
              </Box>

              <Box className="role-detail-chips">
                {filteredUsers.length ? (
                  <>
                    {filteredUsers.slice(0, PAGE_SIZE).map((u) => (
                      <Chip
                        key={u.id}
                        text={`${u.firstname} ${u.lastname}`}
                        size="small"
                      />
                    ))}
                    {filteredUsers.length > PAGE_SIZE && (
                      <span
                        title={filteredUsers
                          .slice(PAGE_SIZE)
                          .map((u) => `${u.firstname} ${u.lastname}`)
                          .join(", ")}
                      >
                        <Chip
                          text={`+${filteredUsers.length - PAGE_SIZE} more`}
                          size="small"
                        />
                      </span>
                    )}
                  </>
                ) : (
                  <span className="role-detail-empty">
                    {userFilter
                      ? "No users match the filter"
                      : "No users assigned"}
                  </span>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </td>
    </tr>
  );
};

const createColumns = ({
  onEdit,
  onToggleExpand,
}: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [{ label: "Edit", icon: "fa-solid fa-pencil" }],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as RoleRecord);
    },
  },
  // Expand toggle column
  {
    field: "_expand",
    title: " ",
    width: 36,
    sortable: false,
    pinned: false,
    noWrap: false,
    renderCell: (dataItem) => {
      const id: number = dataItem.dataItem.id;
      const expanded: boolean = dataItem.dataItem._expanded ?? false;
      return (
        <button
          className={`role-expand-btn${expanded ? " role-expand-btn--open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(id);
          }}
          title={expanded ? "Collapse details" : "Expand details"}
          aria-expanded={expanded}
        >
          <i className="fa-solid fa-chevron-right" />
        </button>
      );
    },
  },
  { field: "id", title: "ID", width: 80, hide: true, pinned: true },
  { field: "name", title: "Role Name", width: 280 },
  { field: "externalSsoGroupId", title: "SSO Group ID", width: 300 },
  {
    field: "userCount",
    title: "Users Assigned",
    width: 150,
    renderCell: (dataItem) => {
      const count: number = dataItem.dataItem.userCount ?? 0;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <i
            className="fa-solid fa-user-group"
            style={{ fontSize: "0.75rem", color: "#00755b" }}
          />
          <span style={{ fontWeight: 600 }}>{count}</span>
        </span>
      );
    },
  },
  {
    field: "resources",
    title: "Resources",
    renderCell: (dataItem) => {
      const items: string[] = dataItem.dataItem.resources ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <Box
          flexDirection="row"
          alignItems="center"
          spacing={3}
          style={{ overflow: "hidden", flexWrap: "nowrap" }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </Box>
      );
    },
  },
];

// Page
const AdminRolesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: categories } = useResourceCategoriesLookupQuery();
  const { data: resources } = useResourcesLookupQuery();

  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());
  const handleToggleExpand = React.useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(
    () => [
      {
        filterType: "text",
        label: "Role Name",
        columnKey: "name",
        placeholder: "e.g. Manager",
      },
      {
        filterType: "text",
        label: "SSO Group ID",
        columnKey: "externalSsoGroupId",
        placeholder: "Azure AD Object ID",
      },
      {
        filterType: "text",
        label: "User Firstname",
        columnKey: "firstname",
        placeholder: "e.g. John",
      },
      {
        filterType: "text",
        label: "User Lastname",
        columnKey: "lastname",
        placeholder: "e.g. Doe",
      },
      {
        filterType: "dropdown",
        label: "Category",
        columnKey: "category",
        placeholder: "Select category",
        options: categories ?? [],
      },
      {
        filterType: "dropdown",
        label: "Resource",
        columnKey: "resource",
        placeholder: "Select resource",
        options: resources ?? [],
      },
    ],
    [categories, resources],
  );

  const [filterParams, setFilterParams] =
    React.useState<UseGetRolesForGridQueryOptions>({ take: 20, skip: 0 });

  const {
    data: roles,
    isFetching: isRolesLoading,
    refetch,
  } = useGetRolesForGridQuery(filterParams);

  const [selectedRole, setSelectedRole] = React.useState<RoleRecord | null>(
    null,
  );

  const columns = React.useMemo(
    () =>
      createColumns({
        onEdit: setSelectedRole,
        onToggleExpand: handleToggleExpand,
      }),
    [handleToggleExpand],
  );

  const enrichedRecords = React.useMemo(
    () =>
      (roles?.records ?? []).map((r: any) => ({
        ...r,
        _expanded: expandedIds.has(r.id),
      })),
    [roles?.records, expandedIds],
  );

  const adapter = useCPGridV2({
    columns,
    data: enrichedRecords,
    showLoader: isRolesLoading,
    totalRecords: roles?.totalRecords ?? 0,
  });

  // How many visible columns does the grid render? Used for detail row colSpan.
  const visibleColCount = columns.filter((c) => !c.hide).length;

  const handleRowRender = React.useCallback(
    (row: React.ReactElement, rowProps: { dataItem: any }) => {
      const id: number = rowProps.dataItem?.id;
      if (!expandedIds.has(id)) return row;
      return (
        <React.Fragment key={id}>
          {row}
          <RoleDetailRow roleId={id} colSpan={visibleColCount} />
        </React.Fragment>
      );
    },
    [expandedIds, visibleColCount],
  );

  return (
    <Box className="admin-roles-page" flexDirection="column">
      <CPGridV2
        name="admin-roles-grid"
        title="Role Management"
        adapter={adapter}
        compact
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetRolesForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        onRefresh={() => queryClient.invalidateQueries("roles-grid")}
        rowRender={handleRowRender}
        renderActions={() => (
          <CPButton
            fillMode="flat"
            size="small"
            onClick={() => setSelectedRole(new RoleRecord())}
          >
            + Add Role
          </CPButton>
        )}
      />

      <RoleDetailsModal
        key={selectedRole?.id}
        selectedRole={selectedRole}
        onClose={() => {
          setSelectedRole(null);
        }}
        onSave={() => {
          setSelectedRole(null);
          queryClient.invalidateQueries("roles-grid");
        }}
      />
    </Box>
  );
};

export { AdminRolesPage };
export default AdminRolesPage;
